import * as React from 'react';
import { Control, Errors, Form } from 'react-redux-form';
import { RouteComponentProps } from 'react-router';
import { CommentViewModel } from '@models/Comment';
import { ProfileViewModel } from '@models/Auth';
import { getDefaultErrorMessages, hasAlpha, hasValue } from '@common/validators';
import { TypeAheadComponent } from '@common/components';
import { Option } from 'react-select';
import { Chip } from '@common/components';

import { PageViewModel, OptionsViewModel } from '@models/common.models';
import FormNames from '@constants/FormNames.enum';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface CommentComponentProperties extends RouteComponentProps<{ id?: number }> {
  readonly commentId?: number;
  readonly comment?: CommentViewModel;
  readonly message: string;
  readonly isActionComplete: boolean;
  readonly hideSave: boolean;
  readonly entityId?: number;
  readonly oldCommentText?: string;
  readonly usersToAction: Array<OptionsViewModel>;
  readonly showCallerNameField?: boolean;
  readonly onSaveClick: (values: CommentViewModel, usersToAction: Array<OptionsViewModel>) => void;
  readonly loadComment: (comment: CommentViewModel) => void;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly userGroupLookup: (query: string, id?: number) => Promise<PageViewModel<ProfileViewModel>>;
  readonly addUser: (user: ProfileViewModel) => void;
  readonly removeUser: (email: string) => void;
}

interface CommentComponentState {
  submitted: boolean;
}

// Component definition
export class CommentComponent extends React.Component<CommentComponentProperties, CommentComponentState> {
  constructor(props: CommentComponentProperties) {
    super(props);
    this.state = {
      submitted: false
    };
  }

  componentDidMount() {
    if (this.props.comment) {
      this.props.loadComment(this.props.comment);
    }
  }

  renderError() {
    if (!this.props.message) {
      return <div />;
    }

    return <div className="error">{this.props.message}</div>;
  }

  renderHideSave() {
    if (this.props.hideSave) {
      return (
        <div className="col-md-12">
          <div className="row end-xs">
            <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
              Save
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="col-md-12">
        <div className="row end-xs mt-10 mb-10">
          <button className="btn btn-secondary" type="button" onClick={() => window.history.back()}>
            Cancel
          </button>
          <button className="btn btn-primary" type="submit" ref={this.props.buttonRef}>
            Save
          </button>
        </div>
      </div>
    );
  }

  renderUserGroup(suggestion: Option<ProfileViewModel>) {
    return (
      <div>
        <div>
          Name: {suggestion.firstName} {suggestion.lastName}
        </div>
        <div>Email: {suggestion.email}</div>
      </div>
    );
  }

  submit(formValues: CommentViewModel, users: Array<OptionsViewModel>) {
    if (!this.state.submitted) {
      this.setState({
        submitted: true
      });
      this.props.onSaveClick(formValues, users);
    }
  }

  render() {
    const hasUsers = this.props.usersToAction.length > 0;
    var users = this.props.usersToAction;
    const editCondition = !hasUsers && this.props.comment && this.props.comment.employees.length > 0;
    if (this.props.comment && editCondition) {
      users = this.props.comment.employees;
    }

    return (
      //  {this.props.comment ? this.props.comment.comment : ''}  "Spoke to customer about..."
      <>
        {this.props.isActionComplete && (
          <div className="row">
            <div className="col-md-12 mb-10">
              <h3> Action Item </h3>
              <div className="row">
                <div className="col-md-6">
                  <p>{this.props.oldCommentText}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        <Form
          model={FormNames.CommentForm}
          className="form-horizontal"
          hideNativeErrors={true}
          validateOn="submit"
          onSubmit={formValues => this.submit(formValues, users)}
        >
          {this.props.isActionComplete ? <h3>I did this to resolve the item...</h3> : <h2>Comment</h2>}
          <div className="row">
            <div className="col-md-12 mb-10">
              <Control.textarea
                model=".comment"
                rows={5}
                placeholder="Spoke to customer about..."
                validators={{ required: hasValue, hasAlpha: hasAlpha }}
                validateOn={['change', 'blur']}
              />
              <Errors
                className="error"
                model=".comment"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <label htmlFor={`.${FormNames.CommentForm}.userGroup`}>User(s) to be Actioned: </label>
              {!editCondition && (
                <Control.text
                  className="mt-10"
                  model=".userGroup"
                  component={(props: { value: ''; onChange: Function }) => {
                    return (
                      <TypeAheadComponent
                        {...props}
                        returnOption={true}
                        onChange={this.props.addUser}
                        renderValue={this.renderUserGroup}
                        valueKey={'id'}
                        labelKey={'email'}
                        getSuggestions={this.props.userGroupLookup}
                        value={props.value}
                      />
                    );
                  }}
                />
              )}

              <ul className="list-unstyled">
                {users.map((user: OptionsViewModel) => (
                  <li key={user.id} className="inline-block">
                    <Chip
                      value={user.value}
                      handleRemove={this.props.removeUser}
                      static={!hasUsers && this.props.comment && this.props.comment.employees.length > 0}
                    />
                  </li>
                ))}
              </ul>
            </div>
            {this.props.showCallerNameField && (
              <div className="col-md-6">
                <label htmlFor={`.${FormNames.CommentForm}.callerName`}>Caller Name: </label>
                <Control.text
                  className="mt-10"
                  model=".callerName"
                  placeholder="Name of caller..."
                  validators={{ required: hasValue, hasAlpha: hasAlpha }}
                  validateOn={['change', 'blur']}
                />
                <Errors
                  className="error"
                  model=".callerName"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
            )}
          </div>
          <div className="form-group">{this.renderHideSave()}</div>
        </Form>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/comments/_components/comment/comment.component.tsx